// Central copy + data for the OORU investor site.

export const NAV_LINKS = [
  { label: "Home", href: "#top" },
  { label: "Vision", href: "#vision" },
  { label: "Traction", href: "#traction" },
  { label: "Originals", href: "#originals" },
  { label: "Partners", href: "#partners" },
  { label: "Contact", href: "#contact" },
];

export const HERO = {
  eyebrow: "Original entertainment IP · Mobile-first generation",
  lines: ["Entertainment", "begins with", "great stories."],
  subtitle:
    "We create original entertainment IP for the mobile-first generation. From compelling microdramas to the next generation of digital storytelling, OORU develops original stories that are designed to engage audiences across languages, platforms and formats.",
  primaryCta: "Partner With OORU",
};

// SECTION 2 — WHO WE ARE (Vision)
export const WHO = {
  label: "01 — Who We Are",
  heading: "Entertainment is evolving. So are the stories people choose to watch.",
  paragraphs: [
    "Today's audiences discover entertainment on their phones before they ever switch on a television. They expect stories that are faster, emotionally engaging, easy to consume and instantly shareable.",
    "OORU was created to build original entertainment IP for this new generation of viewers.",
    "Today, our focus is on microdramas — one of the fastest-growing storytelling formats globally. Tomorrow, our stories may live across new formats, technologies and platforms. But our purpose remains unchanged:",
  ],
  purpose: "To create entertainment that connects with audiences wherever they choose to watch.",
};

// SECTION 3 — WHY MICRODRAMAS
export const WHY = {
  label: "02 — Why Microdramas",
  heading: "A new generation of storytelling.",
  paragraphs: [
    "Microdramas have redefined how stories are consumed.",
    "Short, emotionally engaging episodes are enabling creators to reach audiences at unprecedented speed while opening entirely new opportunities for monetization, localisation and global distribution.",
  ],
  highlight: "At OORU, we believe this is only the beginning.",
  closing:
    "By building original intellectual property from the ground up, we are creating stories that can travel across languages, platforms and markets.",
};

// SECTION 4 — TRACTION
export const TRACTION = {
  label: "03 — Traction",
  heading: "Built on execution.",
  lead: "Ideas matter. Execution matters more. Since entering the microdrama space, our team has consistently delivered high-volume original content for leading platforms.",
  kicker: "OORU Today",
  stats: [
    { value: 20, suffix: "", label: "Original microdrama titles produced", span: "lg:col-span-4" },
    { value: 900, suffix: "+", label: "Episodes created", span: "lg:col-span-4" },
    { display: "₹4 Crore+", label: "Worth of content produced", span: "lg:col-span-4" },
    { value: 4, suffix: "", label: "Engagement hit shows on KUKU", span: "lg:col-span-4" },
    { value: 45, suffix: "", label: "Episodes produced every month", span: "lg:col-span-4" },
    { display: "Multiple", label: "Stories produced across multiple Indian languages", span: "lg:col-span-4" },
  ],
  closer: ["We're not starting from zero.", "We're building on proven execution."],
};

// SECTION 5 — ORIGINALS
export const ORIGINALS = {
  label: "04 — Originals",
  heading: "Original stories. Built to scale.",
  paragraphs: [
    "Every successful entertainment company is powered by great intellectual property.",
    "OORU is developing a growing slate of original concepts designed for mobile-first audiences, with the potential to evolve across languages, formats and distribution platforms.",
  ],
  genreIntro:
    "From romance and family dramas to thrillers, fantasy and emotional human stories, our focus is simple:",
  genres: ["Romance & Family Dramas", "Thrillers", "Fantasy", "Emotional Human Stories"],
  focus: "Create stories people cannot stop watching.",
  images: [
    "/generated/recent-releases/release-3.webp",
    "/generated/recent-releases/release-2.webp",
    "/generated/recent-releases/release-1.webp",
  ],
};

// SECTION 6 — OUR APPROACH
export const APPROACH = {
  label: "05 — Our Approach",
  heading: "Entertainment first. Technology second.",
  lead: "We believe technology should amplify storytelling — not replace it.",
  body: "As new creative technologies continue to evolve, OORU embraces tools that help us create more efficiently, localise more effectively and reach wider audiences, while keeping storytelling at the heart of everything we produce.",
  pull: "Because audiences remember stories — not the software used to create them.",
};

// SECTION 7 — PARTNERS
export const PARTNERS_SECTION = {
  label: "06 — Partners",
  heading: "Let's build the next generation of entertainment together.",
  lead: "We're actively exploring partnerships with:",
  types: [
    "Entertainment Platforms",
    "Investors",
    "IP Owners",
    "Distribution Partners",
    "Creative Collaborators",
    "Global Production Partners",
  ],
  closer:
    "Whether you're looking to invest in original IP, collaborate on new formats or bring compelling stories to new audiences, we'd love to start the conversation.",
};

// SECTION 8 — ABOUT
export const ABOUT_OORU = {
  label: "07 — About OORU",
  heading: "OORU is the original entertainment IP division founded by the team behind Cinimage Studios.",
  paragraphs: [
    "With nearly two decades of experience in content production and a proven track record in large-scale microdrama execution, OORU combines creative storytelling with production expertise to build entertainment properties for the mobile-first era.",
  ],
  ambitionLabel: "Our ambition is simple",
  ambition: "Create stories that audiences love — and intellectual property that endures.",
};

// FINAL CTA
export const CTA = {
  label: "Partner With OORU",
  heading: "Great stories always find their audience.",
  body: "We're building the next chapter of entertainment. If you'd like to be part of that journey — as a partner, investor or collaborator — we'd love to hear from you.",
  email: "partner@ooru.co",
};

export const LOGO_WHITE =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_aa25143f-7dad-47f8-b1eb-aed681fb73cc/artifacts/q2ftd2t2_Ooru%20logo%20-%20English%20White.webp";
export const LOGO_COLOR =
  "https://customer-assets-4nw71qhi.emergentagent.net/job_aa25143f-7dad-47f8-b1eb-aed681fb73cc/artifacts/d5vgsl8x_Ooru%20logo%20-%20English%20SVG.webp";
export const LOGO_COLOR_NAV = "/generated/logos/ooru-color-nav.webp";
export const CINIMAGE_LOGO_NAV = "/generated/logos/cinimage-nav.webp";

export const HERO_VIDEO = "/hero.mp4";
export const HERO_VIDEO_WEBM = "/hero.webm";
export const HERO_VIDEO_MOBILE = "/hero-mobile.mp4";
export const HERO_VIDEO_MOBILE_WEBM = "/hero-mobile.webm";

export const PLATFORMS = [
  {
    name: "Kuku TV",
    source: "Official app icon",
    logo: "/generated/platforms/kuku-tv.webp",
  },
  {
    name: "Quick TV",
    source: "Official app icon",
    logo: "/generated/platforms/quick-tv.webp",
  },
  {
    name: "Bullet",
    source: "Zee Network",
    logo: "/generated/platforms/bullet.webp",
  },
];

export const GEN = {
  whoweare: "/generated/whoweare.png",
  visionCarousel: [
    "/generated/vision-carousel/vision-1.webp",
    "/generated/vision-carousel/vision-2.webp",
    "/generated/vision-carousel/vision-3.webp",
    "/generated/vision-carousel/vision-4.webp",
    "/generated/vision-carousel/vision-5.webp",
  ],
  why: "/generated/why.png",
  whyMasonry: [
    "/generated/why-masonry/masonry-tall.webp",
    "/generated/why-masonry/masonry-wide.webp",
    "/generated/why-masonry/masonry-square.webp",
  ],
  traction: "/generated/traction.png",
  approach: "/generated/approach.png",
  about: "/generated/about.png",
  aboutCarousel: [
    "/generated/about-carousel/about-1.webp",
    "/generated/about-carousel/about-2.webp",
    "/generated/about-carousel/about-3.webp",
  ],
  partners: "/generated/partners.png",
};
